import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useEquinorFlag } from 'utils/featureFlags';
import {
  EQUINOR_DIVISIONS,
  EQUINOR_DIVISIONS_LAYER_DESCRIPTION,
  EQUINOR_DIVISIONS_LAYER_NAME,
  EQUINOR_GRANE_INCIDENT_NAME,
  EQUINOR_GRANE_INCIDENT_TYPE,
  UTSIRA_ISLAND_BOX,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/equinorGraneDivisions';
import {
  EQUINOR_ASSETS_TAB_SEEDS,
  EQUINOR_GRANE_ASSETS_LAYER_TITLE,
  EQUINOR_OIL_SLICK_LAYER_TITLE,
  EQUINOR_SLICK_POLYGONS,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/equinorGraneSpillNotification';
import type {
  CreateIcsIncidentLocationBody,
  IcsIncidentLocation,
} from 'hooks/icsLocation/icsIncidentLocation.types';
import {
  keys as incidentKeys,
  type Incident,
} from 'components/IAP/hooks/incidentHooks';
import {
  findEquinorGraneOrgEvent,
  orgEventKeys,
  useOrgEvents,
} from 'hooks/orgEvents/orgEventHooks';
import { internalLayersKeys } from 'hooks/internalLayer/queryKeys';
import { layerNodesKeys } from 'hooks/layerNode/queryKeys';
import type {
  CreateInternalLayerBody,
  InternalLayer,
  ListInternalLayersFilter,
  ListLayersResponse,
} from 'hooks/internalLayer/types';
import { buildSearchQueryParams } from 'hooks/shared/buildSearchQueryParams';

const ensureLocks = new Map<string, Promise<unknown>>();

async function findExistingIncident(
  groupGuid: string
): Promise<Incident | undefined> {
  const resp = await axios.get<Incident[]>(
    `${config.apiGateway.ics_incidents}/${groupGuid}`
  );
  return (resp.data ?? []).find(
    (incident) => incident.name === EQUINOR_GRANE_INCIDENT_NAME
  );
}

async function ensureIncident({
  groupGuid,
  userGuid,
}: {
  groupGuid: string;
  userGuid: string;
}): Promise<Incident | undefined> {
  const existing = await findExistingIncident(groupGuid);
  if (existing) return existing;

  try {
    const created = await axios.post<Incident>(
      config.apiGateway.createIncident,
      JSON.stringify({
        user_guid: userGuid,
        name: EQUINOR_GRANE_INCIDENT_NAME,
        group_guid: groupGuid,
        incident_types: [EQUINOR_GRANE_INCIDENT_TYPE],
        areas_of_responsibility: [],
        activated_users: [],
      })
    );
    return created.data;
  } catch (error) {
    // Another tab or an earlier run may have created it between our GET and
    // POST; the backend rejects the duplicate name, so re-read instead.
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return findExistingIncident(groupGuid);
    }
    throw error;
  }
}

async function linkOrgEvent({
  incident,
  orgEventId,
}: {
  incident: Incident;
  orgEventId: string;
}): Promise<void> {
  if (incident.related_event_ids?.includes(orgEventId)) return;

  await axios.put(
    `${config.apiGateway.org_events}/incident/${incident.id}/related`,
    { eventIds: [orgEventId] }
  );
}

const DIVISION_LOCATIONS: CreateIcsIncidentLocationBody[] = EQUINOR_DIVISIONS.map(
  (division) => ({
    name: division.name,
    description: division.description,
    resourceType: 'customPolygon' as const,
    geometry: division.geometry,
    properties: {
      polygonColor: division.polygonColor,
      polygonOpacity: division.polygonOpacity,
    },
  })
);

const SLICK_LOCATIONS: CreateIcsIncidentLocationBody[] =
  EQUINOR_SLICK_POLYGONS.map((slick) => ({
    name: slick.name,
    description: slick.description,
    resourceType: 'customPolygon' as const,
    geometry: { type: 'Polygon', coordinates: [slick.ring] },
    properties: {
      polygonColor: slick.polygonColor,
      polygonOpacity: slick.polygonOpacity,
    },
  }));

const ASSET_LOCATIONS: CreateIcsIncidentLocationBody[] =
  EQUINOR_ASSETS_TAB_SEEDS.map((asset) => ({
    name: asset.name,
    description: `${asset.city}, ${asset.state}`,
    resourceType: 'facility' as const,
    geometry: { type: 'Point', coordinates: [asset.lon, asset.lat] },
    properties: {},
  }));

type IncidentLayerSpec = {
  name: string;
  description: string;
  locations: CreateIcsIncidentLocationBody[];
};

const EQUINOR_INCIDENT_LAYERS: IncidentLayerSpec[] = [
  {
    name: EQUINOR_DIVISIONS_LAYER_NAME,
    description: EQUINOR_DIVISIONS_LAYER_DESCRIPTION,
    locations: DIVISION_LOCATIONS,
  },
  {
    name: EQUINOR_OIL_SLICK_LAYER_TITLE,
    description:
      'Synthetic surface slick and shoreline oiling for the Grane release.',
    locations: SLICK_LOCATIONS,
  },
  {
    name: EQUINOR_GRANE_ASSETS_LAYER_TITLE,
    description: 'Equinor facilities, shore bases and recovery vessels.',
    locations: ASSET_LOCATIONS,
  },
];

function ringVertices(geometry: unknown): number[][] {
  const coordinates = (geometry as { coordinates?: unknown } | null)
    ?.coordinates;
  if (!Array.isArray(coordinates)) return [];
  return coordinates.flatMap((ring) => (Array.isArray(ring) ? ring : []));
}

/**
 * Divisions used to slice the offshore plume between Grane and Utsira. Orgs
 * seeded before that change still hold those rings, and the layer already
 * exists by name, so update the features in place rather than re-posting.
 */
async function migrateStaleDivisions(layer: InternalLayer): Promise<boolean> {
  if (!layer.draftVersionId) return false;

  const resp = await axios.get<IcsIncidentLocation[]>(
    `${config.apiGateway.ics_locations}/by-version/${layer.draftVersionId}`
  );
  const byName = new Map(
    (resp.data ?? []).map((location) => [location.name, location])
  );

  const stale = EQUINOR_DIVISIONS.flatMap((division) => {
    const location = byName.get(division.name);
    if (!location) return [];
    const offIsland = ringVertices(location.geometry).some(
      ([lon]) => typeof lon === 'number' && lon < UTSIRA_ISLAND_BOX.minLon
    );
    return offIsland ? [{ division, id: location.id }] : [];
  });
  if (stale.length === 0) return false;

  for (const { division, id } of stale) {
    await axios.patch(`${config.apiGateway.ics_locations}/${id}`, {
      name: division.name,
      description: division.description,
      geometry: division.geometry,
      properties: {
        polygonColor: division.polygonColor,
        polygonOpacity: division.polygonOpacity,
      },
    });
  }
  return true;
}

async function ensureIncidentLayers({
  groupGuid,
  incidentId,
}: {
  groupGuid: string;
  incidentId: string;
}): Promise<boolean> {
  const resp = await axios.get<ListLayersResponse<InternalLayer>>(
    config.apiGateway.internal_layers,
    {
      params: buildSearchQueryParams<ListInternalLayersFilter>({
        filter: { groupGuid, incidentId },
      }),
    }
  );
  const byName = new Map(
    (resp.data?.data ?? []).map((layer) => [layer.name, layer])
  );

  let changed = false;
  for (const spec of EQUINOR_INCIDENT_LAYERS) {
    const existing = byName.get(spec.name);
    if (existing) {
      if (spec.name === EQUINOR_DIVISIONS_LAYER_NAME) {
        changed = (await migrateStaleDivisions(existing)) || changed;
      }
      if (existing.defaultVisible !== true) {
        await axios.patch(`${config.apiGateway.internal_layers}/${existing.id}`, {
          defaultVisible: true,
        });
        changed = true;
      }
      continue;
    }

    const body: CreateInternalLayerBody = {
      groupGuid,
      incidentId,
      name: spec.name,
      description: spec.description,
      defaultVisible: true,
      icsIncidentLocations: spec.locations,
    };
    await axios.post<InternalLayer>(config.apiGateway.internal_layers, body);
    changed = true;
  }

  return changed;
}

async function ensureEquinorIncidentForGroup({
  groupGuid,
  userGuid,
  orgEventId,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  orgEventId: string | undefined;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<boolean> {
  const incident = await ensureIncident({ groupGuid, userGuid });
  if (!incident) return false;

  await queryClient.invalidateQueries({
    queryKey: incidentKeys.all(groupGuid),
  });

  if (orgEventId) {
    await linkOrgEvent({ incident, orgEventId });
    await queryClient.invalidateQueries({
      queryKey: orgEventKeys.incidentRelated(incident.id),
    });
  }

  const layersChanged = await ensureIncidentLayers({
    groupGuid,
    incidentId: incident.id,
  });
  if (layersChanged) {
    await queryClient.invalidateQueries({ queryKey: internalLayersKeys.all() });
    await queryClient.invalidateQueries({ queryKey: layerNodesKeys.all() });
  }

  return !!orgEventId;
}

/**
 * Seeds the Grane incident workspace and the three internal layers the map
 * needs there: Division A-D, the surface slick, and the Equinor assets.
 *
 * Unlike the org event, incidents carry no demo_key, so the incident name is
 * the idempotency handle and each layer is matched by name within that
 * incident.
 *
 * The org event is seeded by a sibling hook in the same render pass, so on a
 * first-ever load it may not exist yet. Reading it from the query rather than
 * fetching it here means this re-runs and completes the link as soon as it
 * lands, instead of leaving the workspace unlinked until the next page load.
 */
export function useEnsureEquinorGraneIncident() {
  const equinorEnabled = useEquinorFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);
  const orgEventsQuery = useOrgEvents({ enabled: !!equinorEnabled });
  const orgEventId = findEquinorGraneOrgEvent(orgEventsQuery.data)?.id;

  useEffect(() => {
    if (!equinorEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `equinor-incident:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        return;
      }

      const promise = ensureEquinorIncidentForGroup({
        groupGuid,
        userGuid,
        orgEventId,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        const complete = await promise;
        if (!cancelled && complete) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure Equinor Grane incident', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [equinorEnabled, groupGuid, userGuid, orgEventId, queryClient]);
}
